<template>
  <generic-modal title="Filter meters" width="420px" @close="$emit('close')" @submit="apply">
    <form-field label="Bron" type="select" null-option="Alle" :options="SOURCE_OPTIONS" v-model="draft.source" />
    <form-field label="Status" type="select" null-option="Alle" :options="ALIVE_OPTIONS" v-model="draft.alive" />
    <form-field
      label="Woco"
      type="multi"
      mode="tags"
      placeholder="Alle wocos"
      :options="customerOptions"
      v-model="draft.customer_ids"
    />
    <form-field
      label="SIM-provider"
      type="select"
      null-option="Alle"
      :options="SIM_PROVIDER_OPTIONS"
      v-model="draft.sim_provider"
    />
    <div class="tw-text-xs tw-text-gray-500">
      SIM-provider volgens onze administratie: bij C4A-meters is de SIM daar meestal niet bekend.
    </div>
    <button class="tw-text-xs tw-text-blue-500 hover:tw-text-blue-700 tw-mt-2" @click="clear">Wis filters</button>
  </generic-modal>
</template>

<script setup>
import { computed, reactive } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import FormField from '@/components/shared/FormField.vue'
import GenericModal from '@/components/shared/GenericModal.vue'

// filters: { source?, c4a_account?, customer_ids?, alive?, sim_provider? }, the query parameters of /meters
const props = defineProps({
  filters: { type: Object, required: true },
})

const emit = defineEmits(['apply', 'close'])

// Source and C4A account in one select: 'c4a:solarminds' sets both
const SOURCE_OPTIONS = [
  { value: 'polling', label: 'Polling' },
  { value: 'c4a', label: 'C4A (alle)' },
  { value: 'c4a:staedion', label: 'C4A Staedion' },
  { value: 'c4a:solarminds', label: 'C4A Solarminds' },
  { value: 'solaredge', label: 'SolarEdge' },
]
const ALIVE_OPTIONS = [
  { value: 'true', label: 'Online' },
  { value: 'false', label: 'Offline' },
]
const SIM_PROVIDER_OPTIONS = [
  { value: '1nce', label: '1NCE' },
  { value: 'wireless_logic', label: 'Wireless Logic' },
]

// Edit a copy, apply only on submit
const draft = reactive({
  source: props.filters.c4a_account ? `c4a:${props.filters.c4a_account}` : props.filters.source ?? '',
  alive: props.filters.alive ?? '',
  customer_ids: [...(props.filters.customer_ids ?? [])],
  sim_provider: props.filters.sim_provider ?? '',
})

const clear = () => Object.keys(draft).forEach((key) => { draft[key] = Array.isArray(draft[key]) ? [] : '' })

const apply = () => {
  const [source, c4aAccount] = draft.source.split(':')
  const next = {
    source,
    c4a_account: c4aAccount,
    alive: draft.alive,
    customer_ids: draft.customer_ids,
    sim_provider: draft.sim_provider,
  }
  Object.keys(next).forEach((key) => { if (!next[key] || next[key].length === 0) delete next[key] })
  emit('apply', next)
}

const { data: customers } = useQuery({
  queryKey: ['meterCustomers'],
  queryFn: async () => (await fetchData(`${READINGS_API_BASE}/meters/customers`))?.data ?? [],
  refetchOnWindowFocus: false,
  staleTime: 60 * 60 * 1000,
})

const customerOptions = computed(() =>
  (customers.value ?? []).map((c) => ({
    value: c.id,
    label: `${c.name || `(zonder naam, #${c.id})`} (${c.meter_count})`,
  }))
)
</script>
